import React, { useState, useEffect } from 'react';
import { Modal } from './Components';
import { LoadingSpinner } from './Components';
import { announcementService } from '../../services';

const AnnouncementModal = ({ isOpen, onClose }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAnnouncements();
    }
  }, [isOpen]);

  // Add rich text styles to document head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .rich-text-content h1, .rich-text-content h2, .rich-text-content h3, 
      .rich-text-content h4, .rich-text-content h5, .rich-text-content h6 {
        font-weight: bold !important;
        margin-top: 1rem !important;
        margin-bottom: 0.5rem !important;
      }
      .rich-text-content h1 { font-size: 1.5rem !important; }
      .rich-text-content h2 { font-size: 1.375rem !important; }
      .rich-text-content h3 { font-size: 1.25rem !important; }
      .rich-text-content h4 { font-size: 1.125rem !important; }
      .rich-text-content p {
        margin-bottom: 0.75rem !important;
        line-height: 1.6 !important;
      }
      .rich-text-content ul, .rich-text-content ol {
        margin-left: 1.5rem !important;
        margin-bottom: 0.75rem !important;
        padding-left: 1rem !important;
      }
      .rich-text-content li {
        margin-bottom: 0.25rem !important;
        list-style-type: disc !important;
      }
      .rich-text-content ol li {
        list-style-type: decimal !important;
      }
      .rich-text-content strong, .rich-text-content b {
        font-weight: bold !important;
      }
      .rich-text-content em, .rich-text-content i {
        font-style: italic !important;
      }
      .rich-text-content a {
        color: #3b82f6 !important;
        text-decoration: underline !important;
      }
      .rich-text-content blockquote {
        border-left: 4px solid #e5e7eb !important;
        padding-left: 1rem !important;
        margin: 1rem 0 !important;
        font-style: italic !important;
        background-color: #f9fafb !important;
        padding: 0.75rem !important;
        border-radius: 0.375rem !important;
      }
      .rich-text-content pre {
        background-color: #f3f4f6 !important;
        padding: 0.75rem !important;
        border-radius: 0.375rem !important;
        overflow-x: auto !important;
        margin: 0.75rem 0 !important;
        border: 1px solid #e5e7eb !important;
      }
      .rich-text-content code {
        background-color: #f3f4f6 !important;
        padding: 0.125rem 0.25rem !important;
        border-radius: 0.25rem !important;
        font-family: monospace !important;
        font-size: 0.875rem !important;
      }
      .rich-text-content img {
        max-width: 100% !important;
        height: auto !important;
        margin: 0.75rem 0 !important;
        border-radius: 0.375rem !important;
      }
      .rich-text-content table {
        width: 100% !important;
        border-collapse: collapse !important;
        margin: 0.75rem 0 !important;
      }
      .rich-text-content th, .rich-text-content td {
        border: 1px solid #e5e7eb !important;
        padding: 0.5rem !important;
        text-align: left !important;
      }
      .rich-text-content th {
        background-color: #f9fafb !important;
        font-weight: bold !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await announcementService.getAnnouncements({ limit: 10, page: 1 });

      if (response.data.success) {
        setAnnouncements(response.data.data.announcements || []);
      } else {
        setAnnouncements([]);
        setError('Failed to fetch announcements');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch announcements');
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely render HTML content
  const createMarkup = (html) => {
    return { __html: html || '' };
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Announcements"
      size="2xl"
    >
      <div className="max-h-150 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-5">
            <LoadingSpinner message="Loading announcements..." />
          </div>
        ) : error ? (
          <div className="text-center py-5">
            <p className="text-red-600">{error}</p>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-gray-500">No announcements available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="border border-gray-200 rounded-lg p-3 bg-gray-50 relative"
              >
                <h4 className="text-lg font-semibold text-red-600 mb-2">
                  {announcement.title}
                </h4>
                <div
                  className="text-gray-700 rich-text-content"
                  style={{
                    lineHeight: '1.6',
                  }}
                  dangerouslySetInnerHTML={createMarkup(announcement.description)}
                />
                <div className="absolute bottom-2 right-2 text-sm text-gray-500">
                  <span>Posted on: {formatDate(announcement.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AnnouncementModal;